import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shell } from '../components/Layout.js';
import { useAuth } from '../state/auth.js';
import { api, ApiError } from '../api/client.js';
import { useDocumentMeta } from '../lib/document-meta.js';
import { useT } from '../state/i18n.js';
import type { AccessMode, CreateProposalResponse, MyProposalsResponse } from '@cloudbtl/shared';
import { trackSiteEvent } from '../lib/site-tracker.js';

const ACCEPTED = '.pdf,.html,.htm,application/pdf,text/html';

// 로컬/상대 경로 <img src> 찾기 — 방문자 브라우저에선 해석 안 돼 깨져 보인다.
// 정상: http(s)://, //(프로토콜 상대), data:(인라인 base64). 그 외 = 로컬/상대 = 깨짐.
export function findLocalImageSrcs(html: string): string[] {
  const bad: string[] = [];
  const re = /<img\b[^>]*?\ssrc\s*=\s*["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    const src = (m[1] ?? '').trim();
    if (src && !/^(https?:)?\/\//i.test(src) && !/^data:/i.test(src)) bad.push(src);
  }
  return bad;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AppPage(): JSX.Element {
  const t = useT();
  useDocumentMeta({ title: t({ ko: '문서 올리기', en: 'Upload a document' }), noindex: true });
  const auth = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [htmlText, setHtmlText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [accessMode, setAccessMode] = useState<AccessMode>('public');
  const [allowedDomains, setAllowedDomains] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CreateProposalResponse['proposal'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [proposals, setProposals] = useState<MyProposalsResponse['proposals']>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (auth.loading) return;
    if (!auth.user) return;
    api
      .myProposals()
      .then((r) => setProposals(r.proposals))
      .catch(() => setProposals([]));
  }, [auth.loading, auth.user]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    // 파일 모드=선택 파일, 붙여넣기 모드=HTML 텍스트를 클라이언트서 File로 감싸 동일 업로드에 태움.
    let payload: File | null = file;
    if (mode === 'paste') {
      if (!htmlText.trim()) {
        setError(t({ ko: 'HTML을 먼저 붙여넣으세요.', en: 'Paste some HTML first.' }));
        return;
      }
      const name = `${(title.trim() || 'page').replace(/[^\w가-힣.-]+/g, '-').slice(0, 60)}.html`;
      payload = new File([htmlText], name, { type: 'text/html' });
    }
    if (!payload) {
      setError(
        t({ ko: '먼저 파일을 선택하세요.', en: 'Choose a file first.' }),
      );
      return;
    }
    // 로컬 경로 이미지 경고 — HTML 문서만. 방문자에겐 깨지므로 발행 전 고지(하드블록 아님).
    const isHtml = mode === 'paste' || /\.html?$/i.test(payload.name) || payload.type === 'text/html';
    if (isHtml) {
      const html = mode === 'paste' ? htmlText : await payload.text().catch(() => '');
      const bad = findLocalImageSrcs(html);
      if (bad.length > 0) {
        const sample = bad.slice(0, 3).join(', ') + (bad.length > 3 ? ` …(+${bad.length - 3})` : '');
        const proceed = window.confirm(
          t({
            ko: `로컬/상대 경로 이미지 ${bad.length}개가 있어요 (${sample}).\n방문자 화면에선 깨져 보입니다. 이미지는 호스팅 URL(예: cloudbtl image add로 업로드)이나 base64로 넣으세요.\n\n그래도 이대로 발행할까요?`,
            en: `Found ${bad.length} local/relative image path(s) (${sample}).\nThese will appear broken to visitors. Use a hosted URL (e.g. upload via "cloudbtl image add") or inline base64.\n\nPublish anyway?`,
          }),
        );
        if (!proceed) return;
      }
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('pdf', payload);
      if (title.trim()) form.append('title', title.trim());
      if (description.trim()) form.append('description', description.trim());
      form.append('accessMode', accessMode);
      if (accessMode === 'org') {
        form.append('allowedDomains', allowedDomains);
        if (accessCode.trim()) form.append('accessCode', accessCode.trim());
      } else if (accessMode === 'passcode') {
        form.append('accessCode', accessCode.trim());
      }
      const created = await api.uploadProposal(form);
      trackSiteEvent('document_uploaded', {
        documentType: mode === 'paste' ? 'html' : payload.type || 'unknown',
        accessMode,
      });
      trackSiteEvent('share_link_created', { accessMode });
      setResult(created.proposal);
      setFile(null);
      setHtmlText('');
      setTitle('');
      setAccessMode('public');
      setAllowedDomains('');
      setAccessCode('');
      if (fileInput.current) fileInput.current.value = '';
      if (auth.user) {
        const refreshed = await api.myProposals();
        setProposals(refreshed.proposals);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t({ ko: '업로드에 실패했습니다.', en: 'Upload failed.' }),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <section className="upload-panel">
        <h1>{t({ ko: '문서 추가하기', en: 'Add your document' })}</h1>
        <p>
          {auth.user
            ? t({
                ko: '아무 파일이나 올려주세요. 추적 가능한 공유 링크를 생성합니다. (문서는 뷰어에서 열람 추적, 그 외 파일은 다운로드로 공유)',
                en: 'Upload any file. We generate a tracked share link. (Documents get read-tracking in the viewer; other files are shared as downloads.)',
              })
            : t({
                ko: 'PDF 또는 HTML 파일을 올려주세요. 추적 가능한 공유 링크를 생성합니다. (로그인하면 모든 파일)',
                en: 'Drop a PDF or HTML file. We generate a tracked share link. (Sign in to share any file.)',
              })}
        </p>
        <form onSubmit={onSubmit}>
          <label>
            {t({ ko: '제목 (선택)', en: 'Title (optional)' })}
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} />
          </label>
          <label>
            {t({ ko: '설명 (선택)', en: 'Description (optional)' })}
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder={t({
                ko: '공유 링크를 여는 사람에게 보일 설명',
                en: 'Shown to anyone who opens the share link',
              })}
            />
          </label>
          <div className="input-mode-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'file'}
              className={mode === 'file' ? 'active' : ''}
              onClick={() => setMode('file')}
            >
              {t({ ko: '파일 업로드', en: 'Upload file' })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'paste'}
              className={mode === 'paste' ? 'active' : ''}
              onClick={() => setMode('paste')}
            >
              {t({ ko: 'HTML 붙여넣기', en: 'Paste HTML' })}
            </button>
          </div>
          {mode === 'paste' ? (
            <label>
              HTML
              <textarea
                className="html-paste"
                value={htmlText}
                onChange={(e) => setHtmlText(e.target.value)}
                placeholder={t({
                  ko: '<!doctype html> … 여기에 HTML을 붙여넣으세요',
                  en: '<!doctype html> … paste your HTML here',
                })}
                rows={10}
                spellCheck={false}
              />
              <span className="file-summary">
                {htmlText.trim()
                  ? `${formatBytes(new Blob([htmlText]).size)} · text/html`
                  : t({
                      ko: 'Claude가 만든 HTML을 그대로 붙여넣으면 됩니다',
                      en: 'Paste HTML generated by Claude, as-is',
                    })}
              </span>
            </label>
          ) : (
            <div
              className={`file-field dropzone${dragging ? ' dragging' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => fileInput.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInput.current?.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const dropped = e.dataTransfer.files?.[0];
                // 로그인 상태면 임의 파일 허용(서버가 octet-stream+attachment 로만 서빙).
                // 미로그인은 문서(PDF·HTML)만 — 임의 파일 업로드는 책임 귀속을 위해 로그인 필요.
                if (dropped && (auth.user || /\.(pdf|html?)$/i.test(dropped.name))) {
                  setFile(dropped);
                  setError(null);
                } else if (dropped) {
                  setError(
                    t({
                      ko: '미로그인 상태에선 PDF·HTML만 올릴 수 있습니다. 로그인하면 모든 파일을 공유할 수 있어요.',
                      en: 'Signed out, only PDF and HTML are allowed. Sign in to share any file.',
                    }),
                  );
                }
              }}
            >
              <input
                ref={fileInput}
                type="file"
                accept={auth.user ? undefined : ACCEPTED}
                hidden
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              <span className="file-summary">
                {file
                  ? `${file.name} · ${formatBytes(file.size)}`
                  : dragging
                    ? t({ ko: '놓아서 업로드', en: 'Drop to upload' })
                    : auth.user
                      ? t({
                          ko: '아무 파일이나 끌어다 놓거나 클릭해서 선택 (최대 50 MB)',
                          en: 'Drag any file here, or click to browse (max 50 MB)',
                        })
                      : t({
                          ko: 'PDF·HTML 파일을 끌어다 놓거나 클릭해서 선택 (최대 50 MB) · 로그인하면 모든 파일',
                          en: 'Drag a PDF or HTML file, or click to browse (max 50 MB) · sign in for any file',
                        })}
              </span>
            </div>
          )}
          <label>
            {t({ ko: '누가 열 수 있나요', en: 'Who can open it' })}
            <select
              value={accessMode}
              onChange={(e) => setAccessMode(e.target.value as AccessMode)}
            >
              <option value="public">
                {t({ ko: '링크가 있는 누구나', en: 'Anyone with the link' })}
              </option>
              <option value="passcode">
                {t({ ko: '비밀번호(패스코드) 필요', en: 'Passcode required' })}
              </option>
              <option value="org">
                {t({ ko: '조직 전용 (Google 인증)', en: 'Organization only (Google verified)' })}
              </option>
            </select>
          </label>
          {accessMode === 'org' ? (
            <>
              <label>
                {t({ ko: '허용할 이메일 도메인', en: 'Allowed email domains' })}
                <input
                  required
                  value={allowedDomains}
                  onChange={(e) => setAllowedDomains(e.target.value)}
                  placeholder="clientcorp.com, sweetspot.co.kr"
                />
              </label>
              <span className="file-summary">
                {t({
                  ko: '수신자는 Google 로그인으로 소속을 증명합니다. 여러 도메인은 쉼표로 구분하세요.',
                  en: 'Recipients prove membership by signing in with Google. Comma-separate multiple domains.',
                })}
              </span>
              <label>
                {t({
                  ko: '비밀번호(패스코드) 대체 수단 (선택)',
                  en: 'Passcode fallback (optional)',
                })}
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  minLength={6}
                  placeholder={t({
                    ko: 'Google 계정이 없는 수신자용',
                    en: 'For recipients without a Google account',
                  })}
                />
              </label>
            </>
          ) : null}
          {accessMode === 'passcode' ? (
            <label>
              {t({ ko: '비밀번호(패스코드)', en: 'Passcode' })}
              <input
                type="text"
                required
                minLength={6}
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
              />
            </label>
          ) : null}
          <button className="cta-primary" type="submit" disabled={busy}>
            {busy
              ? t({ ko: '업로드 중…', en: 'Uploading…' })
              : t({ ko: '공유 링크 만들기', en: 'Create share link' })}
          </button>
          {error ? <p className="status err">{error}</p> : null}
        </form>

        {result ? (
          <div className="upload-result">
            <h2>{t({ ko: '공유 링크가 준비되었습니다', en: 'Share link ready' })}</h2>
            <p>
              {t({
                ko: '이 링크를 보내세요. 누가 열었고 얼마나 오래 읽었는지 추적합니다.',
                en: 'Send this link. We track who opens and how long they read.',
              })}
            </p>
            {result.secretWarnings && result.secretWarnings.length > 0 ? (
              <div className="secret-warning" role="alert">
                <strong>
                  {t({
                    ko: '⚠️ 이 문서에 API 키·시크릿으로 보이는 값이 있습니다',
                    en: '⚠️ This document appears to contain API keys or secrets',
                  })}
                </strong>
                <p>
                  {t({
                    ko: '공유 전에 아래 값을 제거하거나 즉시 재발급(rotate)하세요. 링크는 이미 생성됐습니다.',
                    en: 'Remove these before sharing, or rotate them immediately. The link is already created.',
                  })}
                </p>
                <ul>
                  {result.secretWarnings.map((w, i) => (
                    <li key={i}>
                      <code>{w.type}</code> — {w.preview}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.shareUrl ? (
              <ShareLinkRow
                label={t({ ko: '공유 링크', en: 'Share link' })}
                url={result.shareUrl}
              />
            ) : null}
            <ShareLinkRow
              label={t({ ko: '소유자 대시보드', en: 'Owner dashboard' })}
              url={result.dashboardUrl}
            />
            <button
              className="cta-secondary"
              onClick={() =>
                navigate(
                  `/dashboard/${result.id}?key=${encodeURIComponent(new URL(result.dashboardUrl).searchParams.get('key') ?? '')}`,
                )
              }
            >
              {t({ ko: '대시보드 열기', en: 'Open dashboard' })}
            </button>
          </div>
        ) : null}
      </section>

      {auth.user ? (
        <Workspace
          proposals={proposals}
          onChange={async () => {
            const refreshed = await api.myProposals();
            setProposals(refreshed.proposals);
          }}
        />
      ) : (
        <section className="workspace">
          <p>
            <Link to="/login">{t({ ko: '로그인', en: 'Sign in' })}</Link>
            {t({
              ko: '하면 워크스페이스에 문서를 저장할 수 있습니다.',
              en: ' to save documents to your workspace.',
            })}
          </p>
        </section>
      )}
    </Shell>
  );
}

function Workspace({
  proposals,
  onChange,
}: {
  proposals: MyProposalsResponse['proposals'];
  onChange: () => Promise<void>;
}): JSX.Element {
  const t = useT();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  async function saveRename(p: MyProposalsResponse['proposals'][number]): Promise<void> {
    if (!draftTitle.trim()) return;
    setBusy(p.id);
    try {
      const ownerKey = new URL(p.dashboardUrl).searchParams.get('key');
      await api.updateProposal(p.id, ownerKey, draftTitle.trim());
      setEditingId(null);
      await onChange();
    } finally {
      setBusy(null);
    }
  }

  async function remove(p: MyProposalsResponse['proposals'][number]): Promise<void> {
    const confirmed = window.confirm(
      t({
        ko: `"${p.title}"을(를) 삭제할까요?\n\n문서, 모든 공유 링크, 추적된 모든 이벤트가 제거됩니다. 되돌릴 수 없습니다.`,
        en: `Delete "${p.title}"?\n\nThis removes the document, all share links, and all tracked events. This cannot be undone.`,
      }),
    );
    if (!confirmed) return;
    setBusy(p.id);
    try {
      const ownerKey = new URL(p.dashboardUrl).searchParams.get('key');
      await api.deleteProposal(p.id, ownerKey);
      await onChange();
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="workspace">
      <h2>{t({ ko: '내 문서', en: 'Your documents' })}</h2>
      {proposals.length === 0 ? (
        <p className="muted">{t({ ko: '아직 문서가 없습니다.', en: 'No documents yet.' })}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t({ ko: '제목', en: 'Title' })}</th>
              <th>{t({ ko: '링크', en: 'Links' })}</th>
              <th>{t({ ko: '열람', en: 'Opens' })}</th>
              <th>{t({ ko: '방문자', en: 'Visitors' })}</th>
              <th>{t({ ko: '읽은 시간', en: 'Time read' })}</th>
              <th className="actions-col">{t({ ko: '작업', en: 'Actions' })}</th>
            </tr>
          </thead>
          <tbody>
            {proposals.map((p) => (
              <tr key={p.id} aria-busy={busy === p.id}>
                <td>
                  {editingId === p.id ? (
                    <form
                      className="inline-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void saveRename(p);
                      }}
                    >
                      <input
                        autoFocus
                        value={draftTitle}
                        maxLength={160}
                        onChange={(e) => setDraftTitle(e.target.value)}
                      />
                      <button type="submit" className="cta-primary" disabled={busy === p.id}>
                        {t({ ko: '저장', en: 'Save' })}
                      </button>
                      <button type="button" className="link" onClick={() => setEditingId(null)}>
                        {t({ ko: '취소', en: 'Cancel' })}
                      </button>
                    </form>
                  ) : (
                    <>
                      <Link to={`/dashboard/${p.id}`}>{p.title}</Link>
                      <div className="muted small">{p.originalFilename}</div>
                    </>
                  )}
                </td>
                <td>{p.links}</td>
                <td>{p.opens}</td>
                <td>{p.visitors}</td>
                <td>{Math.round(p.totalMs / 1000)}s</td>
                <td className="actions-col">
                  <div className="row-actions">
                    <Link to={`/dashboard/${p.id}`} className="link">
                      {t({ ko: '열기', en: 'Open' })}
                    </Link>
                    <button
                      type="button"
                      className="link"
                      onClick={() => {
                        setEditingId(p.id);
                        setDraftTitle(p.title);
                      }}
                      disabled={busy === p.id || editingId !== null}
                    >
                      {t({ ko: '이름 변경', en: 'Rename' })}
                    </button>
                    <button
                      type="button"
                      className="link danger"
                      onClick={() => void remove(p)}
                      disabled={busy === p.id}
                    >
                      {t({ ko: '삭제', en: 'Delete' })}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ShareLinkRow({ label, url }: { label: string; url: string }): JSX.Element {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="link-row">
      <span className="link-label">{label}</span>
      <input readOnly value={url} />
      <button
        className="cta-secondary"
        onClick={async () => {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? t({ ko: '복사됨', en: 'Copied' }) : t({ ko: '복사', en: 'Copy' })}
      </button>
    </div>
  );
}
